import {
  gameConfig,
  pathsConfig
} from '@/app/config'
import type { SpeedManager } from '@/app/SpeedManager/SpeedManager'
import { THREE } from '@powerplay/core-minigames'
import { linesManager } from './LinesManager'
import type { Athlete } from '../athlete'
import { disciplinePhasesManager } from '@/app/phases/DisciplinePhasesManager'

/**
 * Metoda na vytvorenie ciary s ktorou sa neskor bude manipulovat na jazdenie
 */
export class WorldEnvLinesCreator {

  /** Object 3d na zmenu jazdy a smeru */
  private object3d = new THREE.Object3D()

  /** Percento kde je aktualne hrac */
  private actualPercent = 0

  /** Percento kde bol naposledy hrac */
  private lastPercent = 0

  /** Percento kde bol v cieli hrac */
  private finishPercent = 0

  /** Kolko percent je 1 meter na krivke */
  public oneMeterInPercent = 0

  /** Offset pri idealnu liniu */
  public idealLineOffset = 0

  /** Posledny offset od idealu v zakrute */
  public lastOffsetFromIdealInTurn = 0

  /** trat hraca */
  public playerPath?: THREE.CurvePath<THREE.Vector3>

  /** aktivna cesta hraca */
  public finishPhaseStarted = false

  /** Kolko m ma trat */
  public totalPathLength = 0

  /** Aktualny idealny pomocny offset */
  public actualIdealOffset = new THREE.Vector3(0, 0, 0)

  /**
   * Konstruktor
   * @param animationsManager - manager pre animaciu
   */
  public constructor(
    private callbackDraw: () => unknown,
    private callbackFinish: (actualPos: number, lastPos: number, finishPos: number) => unknown,
    private callbackEndOfPath: () => unknown,
    private athlete: Athlete
  ) {

    this.playerPath = linesManager.getPath()

    this.setActualPercentOnStartIntro()
    this.calculateLineInfo()
    this.setupObject3d()

    this.actualIdealOffset = gameConfig.cameraConfig.idealOffset.clone()

  }

  /**
   * Vratenie aktualnych percent na trati
   * @returns hodnota % na trati
   */
  public getActualPercent(): number {

    return this.actualPercent

  }

  /**
   * Vratenie poslednych percent na trati
   * @returns hodnota % na trati
   */
  public getLastPercent(): number {

    return this.lastPercent

  }

  /**
   * Vratenie cielovych percent na trati
   * @returns hodnota % na trati
   */
  public getFinishPercent(): number {

    return this.finishPercent

  }

  /**
   * Nastavenie pozicie hraca pre emocie
   */
  public setPercentForEmotions(): void {

    this.actualPercent = pathsConfig.positionEmotions

  }

  /**
   * Nastavenie pociatocnej pozicie pre intro
   */
  private setActualPercentOnStartIntro(): void {

    const { min, max } = pathsConfig.positionsIntroOffset
    const randomOffset = min + ((max - min) * Math.random())
    this.actualPercent = pathsConfig.positionsStartIntro + randomOffset

  }

  /**
   * Nastavenie pociatocnej pozicie pre start
   */
  public setActualPercentOnStart(): void {

    this.actualPercent = pathsConfig.positionsStart

    // ked potrebujeme skip do ciela
    if (gameConfig.skipToFinish.active) this.actualPercent = gameConfig.skipToFinish.percent

    this.setupObject3d()

  }

  /**
   * Skontrolovanie, ci sa ma dat finish alebo nie
   */
  private checkFinish(): void {

    if (!this.isFinishEnabled() || this.finishPhaseStarted) return

    this.callbackFinish(
      this.actualPercent * this.totalPathLength,
      this.lastPercent * this.totalPathLength,
      (pathsConfig.positionsFinish) *
                this.totalPathLength
    )

  }

  /**
   * Ci uz je hrac v preslape
   */
  public checkFoul(): void {

    if (this.actualPercent + (pathsConfig.pivotPointOffset * this.oneMeterInPercent) > pathsConfig.positionFoul) {

      this.callbackEndOfPath()

    }

  }

  /**
   * Skontrolovanie, ci uz nie je koniec trate
   */
  private checkEndOfPath(): void {

    if (this.actualPercent >= 1) this.callbackEndOfPath()

  }

  /**
   * Update funkcia
   * @param speedManaager - Speed manager
   * @param isEnd - Ci ide o veci v cieli
   * @param inTop3 - Ci je hrac v top 3
   * @returns - Novy object 3D
   */
  public update(speedManager: SpeedManager, isEnd: boolean, inTop3: boolean): THREE.Object3D {

    if (speedManager.isActive()) {

      // rychlost v m/frame
      const actualSpeed = isEnd ?
        speedManager.getActualSpeedPerFrameOnEnd() :
        speedManager.getActualSpeedPerFrame()

      // rychlost v %/frame
      const speedLockDivider = disciplinePhasesManager.phaseRunning.speedLockCoef
      const actualPercentSpeed = this.oneMeterInPercent * actualSpeed * speedLockDivider

      // musime si zapamatat posledne percento
      this.lastPercent = this.actualPercent

      // pridavame aktualnu rychlost v %/frame
      this.actualPercent += actualPercentSpeed

      // kontrola veci - triggerov
      this.checkFinish()
      this.checkFoul()
      this.checkEndOfPath()

      this.setupObject3d()

    }

    return this.object3d

  }

  /**
   * Ci uz je hrac v cieli
   * @returns boolean
   */
  public isFinishEnabled(): boolean {

    return this.actualPercent > pathsConfig.positionsFinish

  }

  /**
   * Vyratanie dlzky ciary a jedneho metra na ciare
   */
  private calculateLineInfo(): void {

    this.totalPathLength = this.playerPath?.getLength() || 0

    // takisto este potrebujeme 1m kolko je %
    this.oneMeterInPercent = 1 / this.totalPathLength

  }

  /**
   * nastavenie object3d na zaciatku
   */
  private setupObject3d(): void {

    if (this.playerPath === undefined) return

    const point = this.getNewPoint()

    const pointToLookAt = this.getNewPoint(0.0016)
    if (point) {

      this.object3d.position.set(point.x, point.y + gameConfig.yPlayerCorrection, point.z)
      if (pointToLookAt) pointToLookAt.y += gameConfig.yPlayerCorrection

    }

    if (pointToLookAt) {

      this.object3d.lookAt(pointToLookAt)

    }

  }

  /**
   * Vypocitanie noveho bodu pre hraca
   * @returns novy bod alebo undefined ak sa nieco pokazilo
   */
  private getNewPoint(offset = 0): THREE.Vector3 | undefined {

    const point = this.playerPath?.getPointAt(this.actualPercent + offset)
    if (point === undefined) return

    return point

  }

  /** reset */
  public reset(): void {

    this.setActualPercentOnStartIntro()
    this.finishPhaseStarted = false
    this.calculateLineInfo()
    this.setupObject3d()
    this.actualIdealOffset = gameConfig.cameraConfig.idealOffset.clone()
    this.actualPercent = 0

  }

}
